import type { TenantBranding } from "@tenant-theme";
import { buildAppIcons, type AppIconSet } from "./icons";
import type { TenantAppKind } from "@/tenant/types";
import { hasOwnIdentity } from "@/tenant/branding";

/**
 * Per-tenant web app manifest, built at runtime.
 *
 * WHY NOT A STATIC FILE: one build of this shell is served from ONE S3 bucket
 * to EVERY tenant distribution (deploy.sh) — crm.metaship.ai and a customer's
 * white-label domain download byte-identical files. A `dist/manifest.webmanifest`
 * could therefore only ever carry one app's name and icon, and a white-label
 * app must never show the MetaShip mark. The manifest is instead composed from
 * the resolved tenant and injected as a same-origin `blob:` URL.
 *
 * CONSEQUENCE — every URL in the manifest must be ABSOLUTE. Relative URLs
 * resolve against the MANIFEST's own URL, which here is `blob:https://host/uuid`;
 * `start_url: "/"` against that base is not the site root. `start_url`, `scope`
 * and `id` are therefore written out in full from `window.location.origin`, and
 * the icons are `data:` URLs (see ./icons.ts).
 */

export interface ManifestIcon {
  src: string;
  sizes: string;
  type: string;
  purpose: string;
}

export interface WebAppManifest {
  id: string;
  name: string;
  short_name: string;
  description?: string;
  start_url: string;
  scope: string;
  display: string;
  display_override: string[];
  orientation: string;
  theme_color: string;
  background_color: string;
  icons: ManifestIcon[];
}

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

/**
 * BOE-59 — the installed app keeps the phone's own bars, in the app's colour.
 *
 * DEV-1110 asked for `fullscreen` on a phone to hide the clock. On Android that
 * mode is immersive: it hides the status bar AND the navigation bar, so the
 * back / home / recents bar only appeared after a swipe up and the app did not
 * feel like an Android app (Devin, 2026-10-11: "the android bar that is
 * supposed to be at the bottom is not visable unless I slide up"). Android
 * cannot hide one bar without the other, so every install is `standalone`:
 * both bars stay, and `theme_color` (the app's top-bar colour, kept live by
 * the theme-color meta) paints the status bar so it reads as part of the app.
 */
export const DISPLAY = { display: "standalone", display_override: ["standalone", "minimal-ui"] } as const;

function safeColour(value: string | null | undefined, fallback: string): string {
  return value && HEX_COLOUR.test(value) ? value : fallback;
}

/**
 * The installed app's name. A PRODUCT app is one of OUR products on our own
 * domain (crm.metaship.ai) and wears the Metaship name with the product as
 * the qualifier — the same rule the TopBar brand block follows. A WHITE_LABEL
 * app is the customer's own brand, so its name stands alone.
 */
export function appDisplayName(
  kind: TenantAppKind | null,
  name: string,
  branding: Pick<TenantBranding, "identity"> | null = null,
): string {
  return hasOwnIdentity(kind, branding) ? name : `Metaship ${name}`.trim();
}

export function buildManifest(options: {
  kind: TenantAppKind | null;
  name: string;
  origin: string;
  branding: TenantBranding | null;
  icons: AppIconSet | null;
}): WebAppManifest {
  const { kind, name, origin, branding, icons } = options;
  const dark = branding?.defaultTheme !== "light";
  const themeColour = safeColour(branding?.colours?.navbar, dark ? "#1F2937" : "#27567C");
  const backgroundColour = safeColour(branding?.colours?.background, dark ? "#111927" : "#E7EDF3");
  const start = `${origin}/`;

  return {
    // Stable app identity. Pinned to the origin rather than left to default to
    // `start_url`, so a later change to the landing route does not orphan the
    // already-installed app.
    id: start,
    name: appDisplayName(kind, name, branding),
    short_name: name,
    start_url: start,
    scope: start,
    display: DISPLAY.display,
    display_override: [...DISPLAY.display_override],
    orientation: "any",
    theme_color: themeColour,
    background_color: backgroundColour,
    icons: icons
      ? [
          { src: icons.any192, sizes: "192x192", type: "image/png", purpose: "any" },
          { src: icons.any512, sizes: "512x512", type: "image/png", purpose: "any" },
          { src: icons.maskable512, sizes: "512x512", type: "image/png", purpose: "maskable" },
        ]
      : [],
  };
}

let currentManifestUrl: string | null = null;

function upsertLink(rel: string, href: string, attrs: Record<string, string> = {}): void {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  for (const [key, value] of Object.entries(attrs)) el.setAttribute(key, value);
  el.href = href;
}

function upsertMeta(name: string, content: string): void {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/** Publish the manifest as a same-origin blob and point `<link rel="manifest">` at it. */
export function injectManifest(manifest: WebAppManifest): string {
  const blob = new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" });
  const url = URL.createObjectURL(blob);
  if (currentManifestUrl) URL.revokeObjectURL(currentManifestUrl);
  currentManifestUrl = url;
  upsertLink("manifest", url);
  return url;
}

/**
 * Brand the app for installation: manifest, plus the iOS tags — Safari reads
 * `apple-touch-icon` and `apple-mobile-web-app-title` for "Add to Home
 * Screen" instead of the manifest's icons and name.
 *
 * Best-effort by design: it is called after mount and every failure inside is
 * swallowed, because a browser that cannot install the app must still run it.
 */
export async function applyPwaManifest(options: {
  kind: TenantAppKind | null;
  name: string;
  branding: TenantBranding | null;
  origin?: string;
}): Promise<WebAppManifest | null> {
  try {
    const origin = options.origin ?? window.location.origin;
    const icons = await buildAppIcons(
      appDisplayName(options.kind, options.name, options.branding),
      options.branding?.iconUrl || options.branding?.logoLightUrl || null,
      options.branding?.colours?.primary || "#649ECE",
    );
    const manifest = buildManifest({ ...options, origin, icons });
    injectManifest(manifest);

    if (icons) upsertLink("apple-touch-icon", icons.any192, { sizes: "192x192" });
    upsertMeta("apple-mobile-web-app-title", manifest.short_name);
    upsertMeta("application-name", manifest.short_name);
    return manifest;
  } catch {
    return null;
  }
}
